import Service from '@ember/service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Store from '@ember-data/store';
import type CareerPathwaysApiService from 'tio-employee/services/career-pathways-api';
import type ApiService from 'tio-employee/services/api';
import type {
  EmployerContributions,
  StudentLoanSummary,
} from 'tio-employee/services/career-pathways-api';
import type SessionContextService from 'tio-employee/services/session-context';
import type EmployeeService from 'tio-employee/services/employee';
import type PartnerService from 'tio-employee/services/partner';
import type PersonModel from 'tio-common/models/person';
import type AccountModel from 'tio-common/models/account';
import type LoanModel from 'tio-common/models/loan';
import type TasApplicationModel from 'tio-common/models/tas-application';
import type TasProgramInstanceModel from 'tio-common/models/tas-program-instance';
import type EmployeeModel from 'tio-common/models/employee';
import type PslfFormModel from 'tio-common/models/pslf-form';
import type PslfProfileModel from 'tio-common/models/pslf-profile';
import type { LoanAccount } from 'tio-employee/components/reimagined/types';
import {
  getCurrentDemoTasScenarioId,
  getCurrentDemoScenario,
} from 'tio-employee/utils/career-pathways-demo-data';
import { OpenSearchQueryBuilder } from 'tio-common/utils/open-search-builder';
import { OpenSearchResponseBuilder } from 'tio-common/utils/open-search-response-builder';
import type { OpenSearchResponse } from 'tio-common/models/search';
import {
  type RecentApplication,
  ACTIVE_APPLICATION_STATES,
  ACTIVE_INSTANCE_STATES,
  INACTIVE_APPLICATION_STATES,
  INACTIVE_INSTANCE_STATES,
  isActiveInstanceState,
  normalizePaymentFields,
} from 'tio-common/utils/tuition-assistance/application-states';
import { filterEligibleTemplates } from 'tio-common/utils/tuition-assistance/filter-eligibile-templates';
import type TasProgramTemplateModel from 'tio-common/models/tas-program-template';
import type { FutureContributionPayment } from 'tio-employee/services/employee';

export type DataSourceType = 'live' | 'demo' | 'placeholder';

/**
 * Comprehensive tracking of data sources across all reimagined pages.
 * Each field tracks whether it comes from live backend data, demo data, or is a placeholder.
 *
 * DATA SOURCE GAPS (what needs backend work):
 * - careerCapitalScore: No backend API yet - needs career pathways service
 * - skills: No skills tracking in backend - needs career pathways service
 * - careerPlan: No career plan model in backend - needs career pathways service
 * - recommendations: No recommendation engine - needs AI service
 * - optimization: No AI optimizer - needs AI service to analyze best allocation
 */
export interface DataSourceInfo {
  // Employee/Profile Data
  employeeProfile: DataSourceType;
  employerName: DataSourceType;

  // Financial Data
  studentLoans: DataSourceType;
  employerContributions: DataSourceType;
  section127Allocation: DataSourceType;
  section127Used: DataSourceType;

  // Benefit Eligibility
  pslfEligibility: DataSourceType;
  tasEligibility: DataSourceType;

  // Career Data (all demo/placeholder until backend built)
  careerCapitalScore: DataSourceType;
  skills: DataSourceType;
  careerPlan: DataSourceType;
  recommendations: DataSourceType;

  // Optimizer (placeholder until AI built)
  optimization: DataSourceType;
}

/**
 * Live employee profile data extracted from session/models
 */
export interface LiveEmployeeProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  currentRole: {
    title: string;
    department: string;
    startDate: string;
    yearsInRole: number;
  };
  taBenefit: {
    annualMax: number;
    used: number;
    remaining: number;
    eligiblePrograms: number;
  };
  slrBenefit: {
    monthlyContribution: number;
    annualMax: number;
    yearToDate: number;
    lifetime: number;
  };
  pslfEligible: boolean;
  pslfPaymentCount: number;
}

/**
 * Live Section 127 allocation derived from plan data
 */
export interface LiveSection127Info {
  annualLimit: number;
  yearToDateUsed: number;
  remaining: number;
  loanAllocation: number;
  tuitionAllocation: number;
  isLiveData: boolean;
}

/**
 * Normalized contribution payment for the reimagined contribution timeline/table
 */
export interface ContributionPaymentRecord {
  id: string;
  /** Amount in dollars (not cents) */
  amount: number;
  /** Benefit cycle (month/year label) */
  benefitCycle: string;
  /** Paid date (ISO string) or null if still in progress */
  paidDate: string | null;
  /** Servicer account name */
  servicer: string;
  /** Payment status */
  status: 'sent' | 'in_progress' | 'pending' | 'resent' | 'failed';
  /** Whether this is a future payment */
  isFuture: boolean;
}

/**
 * Extract the year string from a payment record.
 * Prefers paidDate, falls back to benefitCycle (YYYY-MM format).
 */
export function getPaymentYear(payment: ContributionPaymentRecord): string | null {
  if (payment.paidDate) {
    return new Date(payment.paidDate).getFullYear().toString();
  }
  if (payment.benefitCycle) {
    const parts = payment.benefitCycle.split('-');
    return parts[0] || null;
  }
  return null;
}

export interface ContributionActionItem {
  id: string;
  title: string;
  description: string;
  badge: string;
  badgeIntent: 'danger' | 'warning' | 'default';
  route: string;
}

export interface ContributionYearlyPayment {
  year: string;
  total: number;
  accounts: { name: string; amount: number }[];
  paymentCount: number;
}

/**
 * Comprehensive contribution history data for the reimagined finance/history page
 */
export interface ContributionHistoryData {
  /** Past + future contribution payments, most recent first */
  payments: ContributionPaymentRecord[];
  /** The last completed payment */
  lastPayment: ContributionPaymentRecord | null;
  /** The next upcoming payment */
  nextPayment: ContributionPaymentRecord | null;
  /** Total lifetime contributions in dollars */
  lifetimeTotal: number;
  /** Number of completed payments */
  paymentCount: number;
  /** Year-to-date contributions in dollars */
  yearToDate: number;
  /** Whether SLR contributions are active */
  isSlrEligible: boolean;
  /** Account receiving contributions (name) */
  accountReceivingName: string | null;
  /** Whether user has multiple accounts (to show change button) */
  hasMultipleAccounts: boolean;
  /** Account alerts from payment validation */
  accountAlerts: { content: string; uploadDocument?: boolean; addressSelect?: boolean }[];
  /** Payment validation errors on next payment */
  paymentValidationErrors: string[];
  /** Action items requiring user attention */
  actionItems: ContributionActionItem[];
  /** Payments grouped by year, sorted descending */
  paymentsByYear: ContributionYearlyPayment[];
  /** Whether this is live data */
  isLiveData: boolean;
  /** JSONAPI pagination metadata (page count, record count, etc.) */
  meta?: Record<string, unknown>;
}

/**
 * Live PSLF data from pslf-profile and pslf-form models
 */
export interface LivePslfData {
  isEligible: boolean;
  isEnrolled: boolean;
  qualifyingPayments: number;
  estimatedForgiveness: number;
  forgivenessDate: string | null;
  currentEmployer: {
    name: string;
    isQualifying: boolean;
    startDate: string | null;
  } | null;
  forms: LivePslfForm[];
  recentActivity: {
    type: 'form_submitted' | 'form_signed' | 'payment_certified' | 'status_change';
    date: string;
    description: string;
  }[];
  isLiveData: boolean;
}

export interface LivePslfForm {
  id: string;
  status: string;
  formType: string;
  employerName: string;
  employmentStartDate: string | null;
  employmentEndDate: string | null;
  borrowerSignedAt: string | null;
  employerSignedAt: string | null;
  submittedAt: string | null;
  createdAt: string;
}

/**
 * Eligible program template for display in the Eligible Benefits section
 */
export interface EligibleTemplate {
  id: string;
  name: string;
  description: string | null;
  isDirectPayProgram: boolean;
  isEnrolled: boolean;
}

/**
 * Live TAS (Tuition Assistance) data
 */
export interface LiveTasData {
  isEligible: boolean;
  participantState: string | null;
  programs: LiveTasProgram[];
  applications: LiveTasApplication[];
  summary: {
    totalApproved: number;
    totalPaid: number;
    pendingCount: number;
    activeCount: number;
    completedCount: number;
  };
  isLiveData: boolean;
}

export interface LiveTasProgram {
  id: string;
  name: string;
  state: string;
  institution: string | null;
  major: string | null;
  startDate: string | null;
  updatedAt: string | null;
  typeClassification: string | null;
}

export interface LiveTasApplication {
  id: string;
  recordType: 'application' | 'instance';
  programInstanceId: string | null;
  programTemplateId: string | null;
  name: string;
  state: string;
  paymentState: string;
  approvedTotal: number;
  requestedTotal: number;
  paidTotal: number;
  courseCashPreApprovedTotal: number | null;
  courseCashFinalApprovedTotal: number | null;
  courseCashUsed: number | null;
  courseCashReserved: number | null;
  coursesBeginDate: string | null;
  coursesEndDate: string | null;
  createdAt: string;
  updatedAt: string | null;
  programName: string | null;
}

/**
 * A proxy object that mimics the TasApplicationModel interface for demo mode.
 * Used by the show route to avoid making real API calls for demo data.
 */
export interface DemoTasApplicationProxy {
  id: string;
  state: string;
  paymentState: string;
  approvedTotal: number;
  createdAt: string;
  displayName: string;
  activeCourses: unknown[];
  tasCourses: unknown[];
  tasAssets: unknown[];
  tasProgramInstance: {
    id: string;
    tasProgramTemplate: {
      programName: string;
      typeClassification: string;
      courseApprovalRequired: boolean;
    };
    fields: Record<string, unknown>;
  } | null;
  fields: Record<string, unknown>;
  applicationName: string;
  programName: string;
  coursesBeginDate: string;
  transitionLog: unknown[];
  customFields: unknown[];
  paymentStateLog: unknown[];
  approvedTotalLog: unknown[];
  coursesApproval: unknown;
  evidenceApproval: unknown;
  isDraft: boolean;
  isApproved: boolean;
  tags: unknown[];
  attachments: unknown[];
  _isDemoProxy: true;
}

export default class ReimaginedDataService extends Service {
  @service declare store: Store;
  @service declare api: ApiService;
  @service declare careerPathwaysApi: CareerPathwaysApiService;
  @service declare sessionContext: SessionContextService;
  @service declare employee: EmployeeService;
  @service declare partner: PartnerService;

  private tasSearchCache?: {
    employeeId: string;
    companyId: string | null;
    fetchedAt: number;
    docs: RecentApplication[];
  };

  private readonly tasSearchCacheTtlMs = 30_000;

  /**
   * Tracked state for data audit mode. When enabled, data source badges are shown
   * and placeholder sections are highlighted with marching ants.
   */
  @tracked isDataAuditEnabled = this.initDataAuditState();

  /**
   * Tracked state for data source docs panel visibility
   */
  @tracked isDocsOpen = false;

  /**
   * The section ID to scroll to when opening docs
   */
  @tracked docsSectionId?: string;

  private initDataAuditState() {
    if (typeof window === 'undefined') {
      return false;
    }
    // Read from localStorage for persistence across page refreshes
    const stored = localStorage.getItem('reimagined-data-audit');
    const isEnabled = stored === 'true';
    // Apply the body class on init to match stored state
    if (isEnabled) {
      document.body.classList.add('reimagined-data-audit');
    }
    return isEnabled;
  }

  /**
   * Toggle data audit mode. This shows/hides data source badges and
   * highlights placeholder sections.
   */
  toggleDataAudit = () => {
    this.isDataAuditEnabled = !this.isDataAuditEnabled;
    if (typeof window !== 'undefined') {
      document.body.classList.toggle('reimagined-data-audit', this.isDataAuditEnabled);
      // Persist to localStorage for page refresh persistence
      localStorage.setItem('reimagined-data-audit', String(this.isDataAuditEnabled));
    }
  };

  /**
   * Open the data source documentation panel, optionally scrolling to a section
   */
  openDocs = (sectionId?: string) => {
    this.docsSectionId = sectionId;
    this.isDocsOpen = true;
  };

  /**
   * Close the data source documentation panel
   */
  closeDocs = () => {
    this.isDocsOpen = false;
    this.docsSectionId = undefined;
  };

  /**
   * Controls whether data source badges (Live Data, Demo Data, Not Connected)
   * are shown on reimagined UI components.
   *
   * Badge visibility is controlled by the Data Audit toggle (warning icon) in the header.
   * Can also be controlled via URL param ?show_badges=true/false.
   *
   * - In demo mode: badges always hidden
   * - In live mode + data audit OFF: badges hidden
   * - In live mode + data audit ON: badges shown
   */
  get showDataSourceBadges() {
    if (typeof window === 'undefined') {
      return false;
    }

    // URL param overrides everything
    const params = new URLSearchParams(window.location.search);
    const badgeParam = params.get('show_badges');
    if (badgeParam === 'true') return true;
    if (badgeParam === 'false') return false;

    // In demo mode, never show badges (demos are for sales/marketing)
    if (this.isDemoMode) {
      return false;
    }

    // In live mode, show badges only when data audit is enabled (tracked property)
    return this.isDataAuditEnabled;
  }

  get isDemoMode() {
    if (typeof window === 'undefined') {
      return false;
    }

    const params = new URLSearchParams(window.location.search);
    const urlHasDemo =
      params.get('demo') === 'true' || params.has('demo_client') || params.has('demo_scenario');

    if (urlHasDemo) {
      // Persist to localStorage so demo mode survives nav transitions that drop query params
      localStorage.setItem('reimagined-demo-mode', 'true');
      return true;
    }

    // Fall back to localStorage (e.g. after internal LinkTo navigation drops query params)
    return localStorage.getItem('reimagined-demo-mode') === 'true';
  }

  // ===========================================================================
  // FEATURE FLAGS — centralized scenario-aware feature visibility
  //
  // In demo mode these come from the scenario. In live mode they come from
  // partner config / employee data. Every reimagined page should use these
  // instead of inlining its own checks.
  // ===========================================================================

  /**
   * Whether Tuition Assistance should be shown to this user.
   * Demo mode → scenario.features.tuitionAssistance
   * Live mode → partner has TAS enabled
   */
  get showTuitionAssistance(): boolean {
    if (this.isDemoMode) {
      return getCurrentDemoScenario().features.tuitionAssistance;
    }
    return this.partner.hasNewTuitionAssistance;
  }

  /**
   * Whether Student Loan Repayment content should be shown.
   * Demo mode → scenario.features.studentLoanRepayment
   * Live mode → partner has contribution plans
   */
  get showStudentLoanRepayment(): boolean {
    if (this.isDemoMode) {
      return getCurrentDemoScenario().features.studentLoanRepayment;
    }
    return this.partner.hasContributions;
  }

  /**
   * Whether PSLF content should be shown.
   * Demo mode → scenario.features.pslf
   * Live mode → partner has PSLF module enabled
   */
  get showPslf(): boolean {
    if (this.isDemoMode) {
      return getCurrentDemoScenario().features.pslf;
    }
    return !!this.partner.isPslfEnabled;
  }

  /**
   * Whether career pathways content should be shown.
   */
  get showCareerPathways(): boolean {
    if (this.isDemoMode) {
      return getCurrentDemoScenario().features.careerPathways;
    }
    return true;
  }

  /**
   * Whether financial wellness content should be shown.
   * Demo mode → scenario features
   * Live mode → partner has wellness plans
   */
  get showFinancialWellness(): boolean {
    if (this.isDemoMode) {
      return getCurrentDemoScenario().features.financialWellness;
    }
    return this.partner.hasWellness;
  }

  /**
   * The full current scenario (for messaging, userState, etc.).
   * Only meaningful in demo mode — returns the 'full' scenario as fallback.
   */
  get currentScenario() {
    return getCurrentDemoScenario();
  }

  async fetchDashboardData() {
    return this.careerPathwaysApi.fetchDashboardData();
  }

  async fetchCareerCapitalScore() {
    return this.careerPathwaysApi.fetchCareerCapitalScore();
  }

  async fetchFinancialSnapshot() {
    if (this.isDemoMode) {
      return this.careerPathwaysApi.fetchFinancialSnapshot();
    }

    const snapshot = await this.careerPathwaysApi.fetchFinancialSnapshot();
    const loansData = await this.fetchLoansData();

    if (!loansData.hasLinkedLoans) {
      return snapshot;
    }

    const totalBalance = loansData.loans.reduce((sum, loan) => sum + loan.balance, 0);
    const totalMonthlyPayment = loansData.loans.reduce((sum, loan) => sum + loan.monthlyPayment, 0);
    const weightedRate = loansData.loans.reduce(
      (sum, loan) => sum + loan.balance * loan.interestRate,
      0
    );
    const interestRate = totalBalance > 0 ? Number((weightedRate / totalBalance).toFixed(1)) : 0;

    const payoffDates = loansData.loans.map((loan) => loan.estimatedPayoff).filter((date) => date);
    const estimatedPayoffDate =
      payoffDates.length > 0
        ? (payoffDates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ??
          snapshot.studentLoans.estimatedPayoffDate)
        : snapshot.studentLoans.estimatedPayoffDate;

    const uniqueServicers = Array.from(new Set(loansData.loans.map((loan) => loan.servicer)));
    const servicer =
      uniqueServicers.length === 1 ? (uniqueServicers[0] ?? 'Loan Servicer') : 'Multiple servicers';

    const studentLoans: StudentLoanSummary = {
      ...snapshot.studentLoans,
      totalBalance,
      monthlyPayment: totalMonthlyPayment,
      interestRate,
      estimatedPayoffDate,
      servicer,
      paymentsMade: snapshot.studentLoans.paymentsMade ?? 0,
    };

    return {
      ...snapshot,
      studentLoans,
    };
  }

  async fetchScoreHistory(months: number) {
    return this.careerPathwaysApi.fetchScoreHistory(months);
  }

  async fetchSection127Recommendation() {
    return this.careerPathwaysApi.fetchSection127Recommendation();
  }

  async fetchLoansData() {
    if (this.isDemoMode) {
      return {
        isDemoMode: true,
        loans: [] as LoanAccount[],
        hasLinkedLoans: false,
        activeAccounts: [] as AccountModel[],
        accountReceivingContributions: null as AccountModel | null,
        personId: null as string | null,
      };
    }

    const personId = this.sessionContext.person?.id;
    if (!personId) {
      return {
        isDemoMode: false,
        loans: [] as LoanAccount[],
        hasLinkedLoans: false,
        activeAccounts: [] as AccountModel[],
        accountReceivingContributions: null as AccountModel | null,
        personId: null as string | null,
      };
    }

    const person = await this.store.findRecord<PersonModel>('person', personId, {
      include: 'accounts.financial-institution,accounts.loans',
      reload: true,
    });

    const activeAccounts = person.activeAccounts || [];
    const accountReceivingContributionsId = person.accountReceivingContributionsId;

    const loans = activeAccounts
      .map((account: AccountModel): LoanAccount => {
        const activeLoans = account.activeLoans || [];
        const loanCount = activeLoans.length;

        const originalBalance = activeLoans.reduce(
          (sum, loan: LoanModel) => sum + (loan.originalAmount || 0),
          0
        );
        const balance = activeLoans.reduce(
          (sum, loan: LoanModel) => sum + (loan.currentBalance || 0),
          0
        );

        const monthlyPayment =
          account.loanMinMonthlyPayment || account.providerRegularMonthlyPayment || 0;

        const nextPaymentDate =
          activeLoans.find((loan) => loan.nextPaymentDate)?.nextPaymentDate ||
          account.providerAttemptAt ||
          new Date().toISOString();

        const interestRate = Number(account.loanInterestAverage || 0);

        const loanClass = activeLoans[0]?.loanClass?.toLowerCase() || '';
        const accountType = loanClass.includes('private')
          ? 'private'
          : loanClass.includes('refinance')
            ? 'refinanced'
            : 'federal';

        // Estimate payoff date from balance, payment, and rate instead of using
        // the next payment date (which is just when the next bill is due, NOT payoff)
        const estimatedPayoff = this.estimatePayoffDate(balance, monthlyPayment, interestRate);

        return {
          id: account.id ?? '',
          servicer: account.institutionName || account.observation?.provider || 'Loan Servicer',
          balance,
          originalBalance: originalBalance || balance,
          interestRate,
          monthlyPayment,
          nextPaymentDate,
          estimatedPayoff,
          isReceivingEmployerHelp: account.id === accountReceivingContributionsId,
          loanCount,
          accountType,
        };
      })
      .filter((loan) => loan.loanCount > 0);

    return {
      isDemoMode: false,
      loans,
      hasLinkedLoans: loans.length > 0,
      activeAccounts,
      accountReceivingContributions: person.accountReceivingContributions ?? null,
      personId,
    };
  }

  /**
   * Simple amortization-based payoff date estimate.
   * Uses balance, monthly payment, and interest rate to project when the loan
   * will be paid off. Falls back to 10 years if inputs are insufficient.
   */
  private estimatePayoffDate(balance: number, monthlyPayment: number, annualRate: number) {
    if (balance <= 0) {
      return new Date().toISOString();
    }

    // If payment is too small to cover interest, estimate 10 years as fallback
    const monthlyRate = annualRate / 100 / 12;
    const monthlyInterest = balance * monthlyRate;

    if (monthlyPayment <= 0 || monthlyPayment <= monthlyInterest) {
      const fallback = new Date();
      fallback.setFullYear(fallback.getFullYear() + 10);
      return fallback.toISOString();
    }

    // Standard amortization: n = -log(1 - (r * PV / PMT)) / log(1 + r)
    let months: number;
    if (monthlyRate > 0) {
      months = Math.ceil(
        -Math.log(1 - (monthlyRate * balance) / monthlyPayment) / Math.log(1 + monthlyRate)
      );
    } else {
      months = Math.ceil(balance / monthlyPayment);
    }

    // Cap at 30 years (360 months) to avoid unreasonable dates
    months = Math.min(months, 360);

    const payoffDate = new Date();
    payoffDate.setMonth(payoffDate.getMonth() + months);
    return payoffDate.toISOString();
  }

  /**
   * Fetches employer contribution data from the employee service
   * Returns live data if available, otherwise falls back to demo data
   */
  async fetchEmployerContributions(): Promise<{
    contributions: EmployerContributions;
    isLiveData: boolean;
  }> {
    if (this.isDemoMode) {
      const snapshot = await this.careerPathwaysApi.fetchFinancialSnapshot();
      return {
        contributions: snapshot.employerContributions,
        isLiveData: false,
      };
    }

    // Wait for the employee contribution data to load
    const summary = this.employee.contributionSummary;

    if (!summary || summary.total === undefined) {
      // No live data available, fall back to demo
      const snapshot = await this.careerPathwaysApi.fetchFinancialSnapshot();
      return {
        contributions: snapshot.employerContributions,
        isLiveData: false,
      };
    }

    // Convert from cents to dollars
    const lifetimeTotal = (summary.total || 0) / 100;

    // Calculate year-to-date from contribution payments
    const currentYear = new Date().getFullYear();
    const ytdPayments = this.employee.contributionPayments.filter((payment) => {
      const paymentYear = new Date(
        payment.line_item_date || payment.line_item_period
      ).getFullYear();
      return paymentYear === currentYear;
    });

    const yearToDate =
      ytdPayments.reduce((total, payment) => total + (payment.amount || 0), 0) / 100;

    // Get the plan annual limit to estimate projected annual
    const plan = this.sessionContext.currentEmployee?.plan;
    const annualLimit = plan?.annualLimit || 5250;

    // Project based on current YTD and time elapsed in year
    const monthsElapsed = new Date().getMonth() + 1;
    const projectedAnnual = Math.min(Math.round((yearToDate / monthsElapsed) * 12), annualLimit);

    return {
      contributions: {
        yearToDate,
        lifetime: lifetimeTotal,
        projectedAnnual,
      },
      isLiveData: true,
    };
  }

  /**
   * Fetches comprehensive contribution history for the reimagined finance/history page.
   * Combines past payments, future payments, account alerts, and eligibility status
   * into a single unified data structure.
   *
   * Uses JSONAPI queries to `accounting-transactions` (with `accounting-line-item` included),
   * giving us server-side pagination, sorting, and filtering.
   */
  async fetchContributionHistory(options?: {
    page?: { number?: number; size?: number };
  }): Promise<ContributionHistoryData> {
    if (this.isDemoMode) {
      return this.getDemoContributionHistory();
    }

    const alerts = this.employee.accountAlerts;
    const person = this.sessionContext.person;
    const employee = this.sessionContext.currentEmployee;
    const cadence = employee?.plan?.cadence;
    const isSlrEligible = !!(cadence && cadence !== 'NEVER');

    // Fetch contribution transactions via JSONAPI and future payments in parallel
    let transactionsResult;
    let futurePayments;

    try {
      [transactionsResult, futurePayments] = await Promise.all([
        this.employee.queryContributionTransactions({
          page: options?.page,
          sort: '-transaction-date',
        }),
        this._fetchFuturePayments(),
      ]);
    } catch (error) {
      console.error(
        '[reimagined-data] Failed to fetch contribution history, falling back to empty state',
        error
      );

      return this.getDemoContributionHistory();
    }

    // Normalize accounting transactions to ContributionPaymentRecord
    const normalizedPast: ContributionPaymentRecord[] = transactionsResult.transactions.map(
      (txn) => {
        const lineItem = txn.accountingLineItem;
        const txnState = (txn.state || '').toLowerCase();
        let status: ContributionPaymentRecord['status'] = 'pending';

        if (txnState === 'failed' || txnState === 'returned') {
          status = 'failed';
        } else if (txnState === 'resent' || txnState === 'retried') {
          status = 'resent';
        } else if (txn.transactionDate) {
          status = 'sent';
        } else if (lineItem?.date) {
          status = 'in_progress';
        }

        return {
          id: txn.id ?? '',
          amount: (txn.amount || 0) / 100,
          benefitCycle: lineItem?.period || lineItem?.date || '',
          paidDate: txn.transactionDate || null,
          servicer: txn.servicer || '',
          status,
          isFuture: false,
        };
      }
    );

    // Normalize future payments (still from legacy preview endpoint for now)
    const normalizedFuture: ContributionPaymentRecord[] = futurePayments.map((payment, index) => ({
      id: `future-${index}`,
      amount: (payment.amount || 0) / 100,
      benefitCycle: payment.period || '',
      paidDate: null,
      servicer: '',
      status: 'pending' as const,
      isFuture: true,
    }));

    // Get payment validation errors from next future payment
    const nextFuture = futurePayments[0];
    const validationErrors =
      nextFuture?.validation_errors
        ?.map((err: { message: string }) => err.message)
        .filter(Boolean) || [];

    // YTD and lifetime are approximate when paginated — the hero component
    // sources authoritative totals from fetchEmployerContributions() instead.
    // These are best-effort for the current page context.
    // Exclude failed payments from totals since they didn't complete.
    const successfulPast = normalizedPast.filter((p) => p.status !== 'failed');
    const currentYear = new Date().getFullYear();
    const ytdPayments = successfulPast.filter((payment) => {
      const dateStr = payment.paidDate || payment.benefitCycle;
      if (!dateStr) return false;
      return new Date(dateStr).getFullYear() === currentYear;
    });
    const yearToDate = ytdPayments.reduce((total, payment) => total + (payment.amount || 0), 0);

    const lifetimeTotal = successfulPast.reduce(
      (total, payment) => total + (payment.amount || 0),
      0
    );

    const accountReceiving = person?.accountReceivingContributions;
    const activeAccounts = person?.activeAccounts || [];

    // Compute action items from account alerts
    const actionItems: ContributionActionItem[] = [];
    if (alerts?.some((alert) => alert.uploadDocument)) {
      actionItems.push({
        id: 'upload-required',
        title: 'Update Loan Information',
        description: 'upload a recent loan document',
        badge: 'Required For Payment',
        badgeIntent: 'danger',
        route: 'authenticated.observability.upload',
      });
    }
    if (alerts?.some((alert) => alert.addressSelect)) {
      actionItems.push({
        id: 'address-needed',
        title: 'Update Payment Address',
        description: 'Select or update the address for your loan servicer',
        badge: 'Due Soon',
        badgeIntent: 'warning',
        route: 'authenticated.observability.upload',
      });
    }

    // Compute payments grouped by year
    const paymentsByYear = this.groupPaymentsByYear(normalizedPast);

    return {
      payments: [...normalizedFuture, ...normalizedPast],
      lastPayment: normalizedPast[0] || null,
      nextPayment: normalizedFuture[0] || null,
      lifetimeTotal,
      paymentCount:
        (transactionsResult.meta as { page?: { total?: number } })?.page?.total ??
        normalizedPast.length,
      yearToDate,
      isSlrEligible,
      accountReceivingName: accountReceiving?.institutionName || null,
      hasMultipleAccounts: activeAccounts.length > 1,
      accountAlerts: alerts,
      paymentValidationErrors: validationErrors,
      actionItems,
      paymentsByYear,
      isLiveData: normalizedPast.length > 0 || isSlrEligible,
    };
  }

  /**
   * Fetches future payments from the legacy preview endpoint.
   * TODO: Move this to a JSONAPI resource when tio-payments data is migrated.
   */
  private async _fetchFuturePayments(): Promise<FutureContributionPayment[]> {
    const employee = this.sessionContext.currentEmployee;
    if (!employee?.plan || employee.plan.cadence === 'NEVER') {
      return [];
    }

    try {
      const result = await this.store
        .adapterFor('employee')
        .getAccountingFuturePayments(employee.id);
      return result || [];
    } catch (error) {
      console.warn('[reimagined-data] Failed to fetch future payments', error);
      return [];
    }
  }

  /**
   * Helper to group payments by year for yearly contribution cards
   */
  private groupPaymentsByYear(payments: ContributionPaymentRecord[]): ContributionYearlyPayment[] {
    const grouped: Record<string, ContributionYearlyPayment> = {};

    payments
      .filter((payment) => !payment.isFuture && payment.status !== 'failed')
      .forEach((payment) => {
        const year = getPaymentYear(payment);
        if (!year) return;

        if (!grouped[year]) {
          grouped[year] = {
            year,
            total: 0,
            accounts: [],
            paymentCount: 0,
          };
        }

        const entry = grouped[year]!;
        entry.total += payment.amount;
        entry.paymentCount += 1;

        // Track by servicer/account
        const servicer = payment.servicer;
        const existingAccount = entry.accounts.find((a) => a.name === servicer);
        if (existingAccount) {
          existingAccount.amount += payment.amount;
        } else {
          entry.accounts.push({ name: servicer, amount: payment.amount });
        }
      });

    // Convert to array and sort by year descending
    return Object.values(grouped).sort((a, b) => parseInt(b.year) - parseInt(a.year));
  }

  /**
   * Demo contribution history data for use in demo mode
   */
  private getDemoContributionHistory(): ContributionHistoryData {
    const demoPayments: ContributionPaymentRecord[] = [
      {
        id: 'demo-1',
        amount: 125,
        benefitCycle: '2026-01',
        paidDate: '2026-01-14',
        servicer: 'MOHELA',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2',
        amount: 125,
        benefitCycle: '2025-12',
        paidDate: '2025-12-14',
        servicer: 'MOHELA',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-3',
        amount: 125,
        benefitCycle: '2025-11',
        paidDate: '2025-11-14',
        servicer: 'MOHELA',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-4',
        amount: 125,
        benefitCycle: '2025-10',
        paidDate: '2025-10-14',
        servicer: 'MOHELA',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-5',
        amount: 125,
        benefitCycle: '2025-09',
        paidDate: '2025-09-14',
        servicer: 'MOHELA',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-6',
        amount: 125,
        benefitCycle: '2025-08',
        paidDate: '2025-08-14',
        servicer: 'MOHELA',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-7',
        amount: 125,
        benefitCycle: '2025-07',
        paidDate: '2025-07-14',
        servicer: 'MOHELA',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-8',
        amount: 125,
        benefitCycle: '2025-06',
        paidDate: '2025-06-14',
        servicer: 'MOHELA',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-9',
        amount: 125,
        benefitCycle: '2025-05',
        paidDate: '2025-05-14',
        servicer: 'MOHELA',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-10',
        amount: 125,
        benefitCycle: '2025-04',
        paidDate: '2025-04-14',
        servicer: 'MOHELA',
        status: 'sent',
        isFuture: false,
      },
      // Resent payment — original March 2025 payment was returned and retried
      {
        id: 'demo-11-resent',
        amount: 125,
        benefitCycle: '2025-03',
        paidDate: '2025-04-28',
        servicer: 'MOHELA',
        status: 'resent',
        isFuture: false,
      },
      // Failed payment — original March 2025 attempt that failed
      {
        id: 'demo-11-failed',
        amount: 125,
        benefitCycle: '2025-03',
        paidDate: '2025-04-14',
        servicer: 'MOHELA',
        status: 'failed',
        isFuture: false,
      },
      {
        id: 'demo-12',
        amount: 125,
        benefitCycle: '2025-02',
        paidDate: '2025-03-14',
        servicer: 'MOHELA',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-13',
        amount: 125,
        benefitCycle: '2025-01',
        paidDate: '2025-02-14',
        servicer: 'MOHELA',
        status: 'sent',
        isFuture: false,
      },
      // 2024 payments
      {
        id: 'demo-2024-12',
        amount: 100,
        benefitCycle: '2024-12',
        paidDate: '2024-12-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2024-11',
        amount: 100,
        benefitCycle: '2024-11',
        paidDate: '2024-11-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2024-10',
        amount: 100,
        benefitCycle: '2024-10',
        paidDate: '2024-10-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2024-09',
        amount: 100,
        benefitCycle: '2024-09',
        paidDate: '2024-09-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2024-08',
        amount: 100,
        benefitCycle: '2024-08',
        paidDate: '2024-08-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2024-07',
        amount: 100,
        benefitCycle: '2024-07',
        paidDate: '2024-07-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2024-06',
        amount: 100,
        benefitCycle: '2024-06',
        paidDate: '2024-06-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2024-05',
        amount: 100,
        benefitCycle: '2024-05',
        paidDate: '2024-05-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2024-04',
        amount: 100,
        benefitCycle: '2024-04',
        paidDate: '2024-04-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2024-03',
        amount: 100,
        benefitCycle: '2024-03',
        paidDate: '2024-03-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2024-02',
        amount: 100,
        benefitCycle: '2024-02',
        paidDate: '2024-02-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2024-01',
        amount: 100,
        benefitCycle: '2024-01',
        paidDate: '2024-01-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      // 2023 payments
      {
        id: 'demo-2023-12',
        amount: 100,
        benefitCycle: '2023-12',
        paidDate: '2023-12-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2023-11',
        amount: 100,
        benefitCycle: '2023-11',
        paidDate: '2023-11-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2023-10',
        amount: 100,
        benefitCycle: '2023-10',
        paidDate: '2023-10-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2023-09',
        amount: 100,
        benefitCycle: '2023-09',
        paidDate: '2023-09-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2023-08',
        amount: 100,
        benefitCycle: '2023-08',
        paidDate: '2023-08-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2023-07',
        amount: 100,
        benefitCycle: '2023-07',
        paidDate: '2023-07-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2023-06',
        amount: 100,
        benefitCycle: '2023-06',
        paidDate: '2023-06-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2023-05',
        amount: 100,
        benefitCycle: '2023-05',
        paidDate: '2023-05-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2023-04',
        amount: 100,
        benefitCycle: '2023-04',
        paidDate: '2023-04-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2023-03',
        amount: 100,
        benefitCycle: '2023-03',
        paidDate: '2023-03-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2023-02',
        amount: 100,
        benefitCycle: '2023-02',
        paidDate: '2023-02-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
      {
        id: 'demo-2023-01',
        amount: 100,
        benefitCycle: '2023-01',
        paidDate: '2023-01-14',
        servicer: 'Nelnet',
        status: 'sent',
        isFuture: false,
      },
    ];

    const nextPayment: ContributionPaymentRecord = {
      id: 'demo-next',
      amount: 125,
      benefitCycle: '2026-02',
      paidDate: null,
      servicer: 'MOHELA',
      status: 'pending',
      isFuture: true,
    };

    // Demo action items
    const demoActionItems: ContributionActionItem[] = [
      {
        id: 'upload-required',
        title: 'Update Loan Information',
        description: 'Your contributions cannot be sent until you upload a new loan document',
        badge: 'Act Now',
        badgeIntent: 'danger',
        route: 'authenticated.observability.upload',
      },
    ];

    // Demo yearly payment groupings
    const demoPaymentsByYear: ContributionYearlyPayment[] = [
      {
        year: '2026',
        total: 250,
        accounts: [{ name: 'MOHELA', amount: 250 }],
        paymentCount: 2,
      },
      {
        year: '2025',
        total: 1200,
        accounts: [
          { name: 'MOHELA', amount: 600 },
          { name: 'Nelnet', amount: 600 },
        ],
        paymentCount: 12,
      },
      {
        year: '2024',
        total: 1200,
        accounts: [{ name: 'Nelnet', amount: 1200 }],
        paymentCount: 12,
      },
      {
        year: '2023',
        total: 1200,
        accounts: [{ name: 'Nelnet', amount: 1200 }],
        paymentCount: 12,
      },
    ];

    return {
      payments: [nextPayment, ...demoPayments],
      lastPayment: demoPayments[0]!,
      nextPayment,
      lifetimeTotal: 4025,
      paymentCount: 38,
      yearToDate: 125,
      isSlrEligible: true,
      accountReceivingName: 'MOHELA',
      hasMultipleAccounts: true,
      accountAlerts: [],
      paymentValidationErrors: [],
      actionItems: demoActionItems,
      paymentsByYear: demoPaymentsByYear,
      isLiveData: false,
    };
  }

  /**
   * Determines comprehensive data source info for all reimagined pages.
   * This tracks which data comes from live backend vs demo/placeholder sources.
   */
  async getDataSourceInfo(): Promise<DataSourceInfo> {
    const loansData = await this.fetchLoansData();
    const contributionsData = await this.fetchEmployerContributions();
    const section127Info = await this.fetchSection127Info();
    // Employee profile is available but not used directly in this method
    // It's used in fetchDashboardDataWithLive instead

    const hasEmployee = !!this.sessionContext.currentEmployee?.id;
    const hasPslfProfile = !!this.sessionContext.currentEmployee?.pslfProfile;
    const hasTasParticipant = !!this.sessionContext.currentEmployee?.tasParticipant;

    return {
      // Employee/Profile Data
      employeeProfile: hasEmployee ? 'live' : 'demo',
      employerName: this.sessionContext.company?.name ? 'live' : 'demo',

      // Financial Data
      studentLoans: loansData.hasLinkedLoans ? 'live' : 'demo',
      employerContributions: contributionsData.isLiveData ? 'live' : 'demo',
      section127Allocation: section127Info.isLiveData ? 'live' : 'demo',
      section127Used: section127Info.isLiveData ? 'live' : 'demo',

      // Benefit Eligibility
      pslfEligibility: hasPslfProfile || this.partner.isPslfEnabled ? 'live' : 'demo',
      tasEligibility: hasTasParticipant || this.partner.hasNewTuitionAssistance ? 'live' : 'demo',

      // Career Data - all placeholder until backend services built
      // GAP: Needs career-pathways microservice or API endpoints in tio-api
      careerCapitalScore: 'placeholder',
      skills: 'placeholder',
      careerPlan: 'placeholder',
      recommendations: 'placeholder',

      // Optimizer - placeholder until AI service built
      // GAP: Needs AI recommendation engine to analyze optimal allocation
      optimization: 'placeholder',
    };
  }

  /**
   * Fetches live employee profile data from session context.
   * This data is already loaded as part of the session.
   */
  fetchLiveEmployeeProfile(): LiveEmployeeProfile | null {
    if (this.isDemoMode) {
      return null;
    }

    const employee = this.sessionContext.currentEmployee;
    const person = this.sessionContext.person;
    const plan = employee?.plan;

    if (!employee?.id) {
      return null;
    }

    // Calculate years in role from employment start date
    const startDate = employee.employmentStartDate || employee.createdAt;
    const msPerYear = 1000 * 60 * 60 * 24 * 365;
    const yearsInRole = startDate
      ? Math.round(((Date.now() - new Date(startDate).getTime()) / msPerYear) * 10) / 10
      : 0;

    // Get contribution YTD from employee service
    const contributionPayments = this.employee.contributionPayments || [];
    const currentYear = new Date().getFullYear();
    const ytdPayments = contributionPayments.filter((payment) => {
      const paymentYear = new Date(
        payment.line_item_date || payment.line_item_period
      ).getFullYear();
      return paymentYear === currentYear;
    });
    const slrYtd = ytdPayments.reduce((total, payment) => total + (payment.amount || 0), 0) / 100;
    const slrLifetime = (this.employee.contributionSummary?.total || 0) / 100;

    return {
      id: employee.id,
      firstName: employee.firstName || person?.firstName || '',
      lastName: employee.lastName || person?.lastName || '',
      email: employee.email || person?.primaryEmail?.email || '',
      currentRole: {
        title: employee.title || 'Employee',
        department: employee.department || '',
        startDate: startDate || new Date().toISOString(),
        yearsInRole,
      },
      taBenefit: {
        annualMax: this.partner.hasNewTuitionAssistance ? 5250 : 0,
        used: 0, // GAP: Need to query TAS applications for used amount
        remaining: this.partner.hasNewTuitionAssistance ? 5250 : 0,
        eligiblePrograms: 0, // GAP: Need program catalog query
      },
      slrBenefit: {
        monthlyContribution: plan?.tiers?.[0]?.amount || 0,
        annualMax: plan?.annualLimit || 0,
        yearToDate: slrYtd,
        lifetime: slrLifetime,
      },
      pslfEligible: !!this.partner.isPslfEnabled,
      // GAP: PSLF payment count would come from pslf-form records or external API
      pslfPaymentCount: 0,
    };
  }

  /**
   * Fetches Section 127 allocation info.
   * Uses live data from plan/contributions when available.
   */
  async fetchSection127Info(): Promise<LiveSection127Info> {
    if (this.isDemoMode) {
      const snapshot = await this.careerPathwaysApi.fetchFinancialSnapshot();
      return {
        annualLimit: snapshot.section127.annualLimit,
        yearToDateUsed: snapshot.section127.yearToDateUsed,
        remaining: snapshot.section127.remaining,
        loanAllocation: snapshot.section127.loanAllocation,
        tuitionAllocation: snapshot.section127.tuitionAllocation,
        isLiveData: false,
      };
    }

    const plan = this.sessionContext.currentEmployee?.plan;
    const annualLimit = plan?.annualLimit || 5250;

    // Get YTD SLR contributions
    const contributionsData = await this.fetchEmployerContributions();
    const slrUsed = contributionsData.contributions.yearToDate;

    // Get YTD TAS usage from approved applications
    const tasUsedData = await this.fetchTasUsedAmount();
    const tasUsed = tasUsedData.yearToDate;

    const yearToDateUsed = slrUsed + tasUsed;
    const remaining = Math.max(0, annualLimit - yearToDateUsed);

    // Current allocation is based on what's been used
    const totalUsed = slrUsed + tasUsed || 1;
    const loanAllocation = slrUsed > 0 ? Math.round((slrUsed / totalUsed) * annualLimit) : 0;
    const tuitionAllocation = annualLimit - loanAllocation;

    const hasLiveData = contributionsData.isLiveData || tasUsedData.isLiveData;

    return {
      annualLimit,
      yearToDateUsed,
      remaining,
      loanAllocation: hasLiveData ? loanAllocation : 0,
      tuitionAllocation: hasLiveData ? tuitionAllocation : annualLimit,
      isLiveData: hasLiveData,
    };
  }

  /**
   * Fetches TAS (Tuition Assistance) used amount for current year.
   * Queries approved TAS applications and sums their pre-approved totals.
   *
   * @returns Object with yearToDate amount and whether data is live
   */
  async fetchTasUsedAmount(): Promise<{ yearToDate: number; isLiveData: boolean }> {
    if (this.isDemoMode) {
      return { yearToDate: 0, isLiveData: false };
    }

    const employee = this.sessionContext.currentEmployee;
    if (!employee?.id) {
      return { yearToDate: 0, isLiveData: false };
    }

    try {
      const companyId = employee.company?.id || this.sessionContext.company?.id || null;
      const docs = await this.fetchTasSearchDocuments({
        employeeId: employee.id,
        companyId: companyId || null,
      });
      const { applications } = this.mapSearchResultsToLiveTasData(docs);
      const yearToDate = this.computeTasUsedAmount(applications);

      return { yearToDate, isLiveData: true };
    } catch (error) {
      console.warn('Failed to fetch TAS used amount:', error);
      return { yearToDate: 0, isLiveData: false };
    }
  }

  /**
   * Fetches comprehensive dashboard data with live employee info where available.
   * Falls back to demo data for career-specific fields.
   */
  async fetchDashboardDataWithLive() {
    const demoData = await this.careerPathwaysApi.fetchDashboardData();
    const liveProfile = this.fetchLiveEmployeeProfile();

    if (!liveProfile || this.isDemoMode) {
      return {
        ...demoData,
        isLiveData: false,
      };
    }

    // Merge live employee data with demo career data
    return {
      ...demoData,
      employee: {
        ...demoData.employee,
        id: liveProfile.id,
        firstName: liveProfile.firstName,
        lastName: liveProfile.lastName,
        email: liveProfile.email,
        currentRole: liveProfile.currentRole,
        taBenefit: liveProfile.taBenefit,
      },
      isLiveData: true,
    };
  }

  /**
   * Fetches live PSLF data from pslf-profile and pslf-form models.
   * Returns enrollment status, qualifying payments, form history, and recent activity.
   */
  async fetchPslfData(): Promise<LivePslfData> {
    if (this.isDemoMode) {
      return this.getDemoPslfData();
    }

    const employee = this.sessionContext.currentEmployee;
    if (!employee?.id) {
      return this.getEmptyPslfData();
    }

    try {
      // Check if PSLF is enabled for the company
      const isPslfEnabled = this.partner.isPslfEnabled;
      if (!isPslfEnabled) {
        return this.getEmptyPslfData();
      }

      // Fetch PSLF profile with forms
      const pslfProfile = employee.pslfProfile as PslfProfileModel | null;

      if (!pslfProfile) {
        // User eligible but hasn't enrolled
        return {
          isEligible: true,
          isEnrolled: false,
          qualifyingPayments: 0,
          estimatedForgiveness: 0,
          forgivenessDate: null,
          currentEmployer: null,
          forms: [],
          recentActivity: [],
          isLiveData: true,
        };
      }

      // Fetch all PSLF forms for this profile
      const forms = await this.store.query<PslfFormModel>('pslf-form', {
        filter: {
          'pslf-profile': pslfProfile.id,
        },
        include: 'attachments',
      });

      // Process forms into our format (filter out any with null IDs)
      const processedForms: LivePslfForm[] = [...forms]
        .filter((form) => form.id !== null)
        .map((form) => ({
          id: form.id as string,
          status: form.status,
          formType: form.formType,
          employerName: form.employerDisplayName || 'Unknown Employer',
          employmentStartDate: form.borrowerFields?.['borrower-employment-start-date'] || null,
          employmentEndDate: form.borrowerFields?.['borrower-employment-end-date'] || null,
          borrowerSignedAt: form.borrowerCompletedAt || null,
          employerSignedAt: form.employerCompletedAt || null,
          submittedAt: form.submittedAt || null,
          createdAt: form.createdAt,
        }));

      // Calculate qualifying payments based on certified forms
      const certifiedForms = processedForms.filter(
        (f) => f.status === 'submitted' || f.status === 're_submitted'
      );

      // Estimate qualifying payments (this would come from MOHELA in production)
      // For now, estimate based on months of employment across submitted forms
      let totalMonths = 0;
      certifiedForms.forEach((form) => {
        if (form.employmentStartDate) {
          const start = new Date(form.employmentStartDate);
          const end = form.employmentEndDate ? new Date(form.employmentEndDate) : new Date();
          const months = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30));
          totalMonths += Math.max(0, months);
        }
      });

      const qualifyingPayments = Math.min(totalMonths, 120);

      // Get current employer from most recent form
      const currentForm = processedForms.find((f) => f.formType === 'currentEmployer');
      const currentEmployer = currentForm
        ? {
            name: currentForm.employerName,
            isQualifying: true, // Assume qualifying if they're filing ECF
            startDate: currentForm.employmentStartDate,
          }
        : null;

      // Build recent activity from forms
      const recentActivity = processedForms
        .filter((f) => f.submittedAt || f.borrowerSignedAt || f.employerSignedAt)
        .map((f) => {
          if (f.submittedAt) {
            return {
              type: 'form_submitted' as const,
              date: f.submittedAt,
              description: `ECF submitted for ${f.employerName}`,
            };
          }
          if (f.employerSignedAt) {
            return {
              type: 'form_signed' as const,
              date: f.employerSignedAt,
              description: `${f.employerName} signed your ECF`,
            };
          }
          return {
            type: 'form_signed' as const,
            date: f.borrowerSignedAt!,
            description: `You signed ECF for ${f.employerName}`,
          };
        })
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, 5);

      // Estimate forgiveness amount from total loan balance
      const loansData = await this.fetchLoansData();
      const estimatedForgiveness = loansData.hasLinkedLoans
        ? loansData.loans.reduce((sum, loan) => sum + loan.balance, 0)
        : 0;

      // Calculate forgiveness date
      const paymentsRemaining = 120 - qualifyingPayments;
      const forgivenessDate =
        paymentsRemaining > 0
          ? new Date(Date.now() + paymentsRemaining * 30 * 24 * 60 * 60 * 1000).toISOString()
          : null;

      return {
        isEligible: true,
        isEnrolled: true,
        qualifyingPayments,
        estimatedForgiveness,
        forgivenessDate,
        currentEmployer,
        forms: processedForms,
        recentActivity,
        isLiveData: true,
      };
    } catch (error) {
      console.warn('Failed to fetch PSLF data:', error);
      return this.getEmptyPslfData();
    }
  }

  private getEmptyPslfData(): LivePslfData {
    return {
      isEligible: Boolean(this.partner.isPslfEnabled),
      isEnrolled: false,
      qualifyingPayments: 0,
      estimatedForgiveness: 0,
      forgivenessDate: null,
      currentEmployer: null,
      forms: [],
      recentActivity: [],
      isLiveData: true,
    };
  }

  private getDemoPslfData(): LivePslfData {
    return {
      isEligible: true,
      isEnrolled: true,
      qualifyingPayments: 84,
      estimatedForgiveness: 45230,
      forgivenessDate: '2029-03-15',
      currentEmployer: {
        name: 'Acme Healthcare',
        isQualifying: true,
        startDate: '2022-01-15',
      },
      forms: [
        {
          id: 'demo-1',
          status: 'submitted',
          formType: 'currentEmployer',
          employerName: 'Acme Healthcare',
          employmentStartDate: '2022-01-15',
          employmentEndDate: null,
          borrowerSignedAt: '2025-12-01',
          employerSignedAt: '2025-12-05',
          submittedAt: '2025-12-06',
          createdAt: '2025-11-28',
        },
        {
          id: 'demo-2',
          status: 'submitted',
          formType: 'priorEmployer',
          employerName: 'City General Hospital',
          employmentStartDate: '2019-06-01',
          employmentEndDate: '2021-12-31',
          borrowerSignedAt: '2024-01-10',
          employerSignedAt: '2024-01-15',
          submittedAt: '2024-01-16',
          createdAt: '2024-01-08',
        },
      ],
      recentActivity: [
        {
          type: 'form_submitted',
          date: '2025-12-06',
          description: 'ECF submitted for Acme Healthcare',
        },
        {
          type: 'form_signed',
          date: '2025-12-05',
          description: 'Acme Healthcare signed your ECF',
        },
        {
          type: 'payment_certified',
          date: '2025-11-15',
          description: '12 payments certified (72→84 total)',
        },
      ],
      isLiveData: false,
    };
  }

  /**
   * Fetches live TAS data including eligibility, programs, and applications.
   */
  async fetchTasData(): Promise<LiveTasData> {
    if (this.isDemoMode) {
      return this.getDemoTasData();
    }

    const employee = this.sessionContext.currentEmployee;
    if (!employee?.id) {
      return this.getEmptyTasData();
    }

    let participantState: string | null = null;

    try {
      if (this.isTasDebugEnabled) {
        console.info('[reimagined-tas] fetchTasData:start', {
          employeeId: employee.id,
          hasNewTuitionAssistance: this.partner.hasNewTuitionAssistance,
        });
      }

      // Check if TAS is enabled
      const hasTas = this.partner.hasNewTuitionAssistance;
      if (!hasTas) {
        if (this.isTasDebugEnabled) {
          console.info('[reimagined-tas] fetchTasData:disabled');
        }
        return this.getEmptyTasData();
      }

      // Get TAS participant (reload if not already present in session context)
      let participant = employee.tasParticipant;
      let refreshedEmployee: EmployeeModel | null = null;
      if (!participant) {
        const employeeRecord = await this.store.findRecord('employee', employee.id, {
          include: 'tas-participant,company',
          reload: true,
        });
        refreshedEmployee = employeeRecord as EmployeeModel;
        participant = refreshedEmployee?.tasParticipant;
      }
      if (this.isTasDebugEnabled) {
        console.info('[reimagined-tas] fetchTasData:participant', {
          participantId: participant?.id ?? null,
        });
      }
      participantState = participant?.state ?? null;
      const companyId =
        employee.company?.id ||
        refreshedEmployee?.company?.id ||
        this.sessionContext.company?.id ||
        null;

      const docs = await this.fetchTasSearchDocuments({
        employeeId: employee.id,
        companyId,
      });
      const { applications, programs, hasApplicationIds, hasApplicationDocs } =
        this.mapSearchResultsToLiveTasData(docs);
      // Only fallback to JSONAPI if we have application-type docs that are missing IDs
      // (data quality issue). Instance-only results are valid — the employee may have
      // Draft program instances with no applications yet.
      if (hasApplicationDocs && !hasApplicationIds) {
        if (this.isTasDebugEnabled) {
          console.info('[reimagined-tas] fetchTasData:missing-application-ids', {
            sampleDoc: docs[0] || null,
            totalDocs: docs.length,
          });
        }
        return this.fetchTasDataFromJsonApi(employee, participantState);
      }
      const summary = this.computeTasSummary(applications);

      return {
        isEligible: true,
        participantState,
        programs,
        applications,
        summary,
        isLiveData: true,
      };
    } catch (error) {
      console.warn('Failed to fetch TAS data:', error);
      return this.fetchTasDataFromJsonApi(employee, participantState);
    }
  }

  private async fetchTasDataFromJsonApi(employee: EmployeeModel, participantState: string | null) {
    const employeeId = employee.id;
    if (!employeeId) {
      return this.getEmptyTasData();
    }

    const applications = await this.store.query<TasApplicationModel>('tas-application', {
      filter: {
        employee: employeeId,
      },
      include: 'tas-program-instance.tas-program-template,tas-courses',
    });

    const processedApps: LiveTasApplication[] = [...applications]
      .filter((app) => app.id !== null)
      .map((app) => ({
        id: app.id as string,
        recordType: 'application' as const,
        programInstanceId: app.tasProgramInstance?.id ?? null,
        name: app.displayName || 'Application',
        state: app.state,
        paymentState: app.paymentState || 'TAS.ApplicationPaymentState.NONE',
        approvedTotal: (app.approvedTotal || 0) / 100,
        requestedTotal: (app.amounts?.requested || 0) / 100,
        paidTotal: (app.amounts?.paid || 0) / 100,
        courseCashPreApprovedTotal: app.fields?.CARD_PRE_APPROVED_TOTAL?.values?.[0] ?? null,
        courseCashFinalApprovedTotal: app.fields?.CARD_FINAL_APPROVED_TOTAL?.values?.[0] ?? null,
        courseCashUsed:
          app.amounts?.course_cash?.used === undefined ? null : app.amounts.course_cash.used / 100,
        courseCashReserved:
          app.amounts?.course_cash?.reserved === undefined
            ? null
            : app.amounts.course_cash.reserved / 100,
        coursesBeginDate: app.coursesBeginDate || null,
        coursesEndDate: app.fields?.COURSES_END_DATE?.values?.[0] || null,
        createdAt: app.createdAt,
        updatedAt: app.updatedAt || null,
        programName: app.tasProgramInstance?.tasProgramTemplate?.programName || null,
        programTemplateId: app.tasProgramInstance?.tasProgramTemplate?.id || null,
      }));

    const programInstances = await this.store.query<TasProgramInstanceModel>(
      'tas-program-instance',
      {
        filter: {
          employee: employeeId,
        },
        include: 'tas-program-template',
      }
    );

    const processedPrograms: LiveTasProgram[] = [...programInstances]
      .filter((pi) => pi.id !== null)
      .map((pi) => ({
        id: pi.id as string,
        name: pi.tasProgramTemplate?.programName || 'Program',
        state: pi.state,
        institution: pi.fields?.SCHOOL_INSTITUTION_NAME?.values?.[0] || null,
        major: pi.fields?.PROGRAM_MAJOR?.values?.[0] || null,
        startDate: pi.fields?.ESTIMATED_PROGRAM_BEGIN?.values?.[0] || null,
        updatedAt: pi.updatedAt || null,
        typeClassification: pi.tasProgramTemplate?.typeClassification || null,
      }));

    return {
      isEligible: true,
      participantState,
      programs: processedPrograms,
      applications: processedApps,
      summary: this.computeTasSummary(processedApps),
      isLiveData: true,
    };
  }

  private computeTasSummary(applications: LiveTasApplication[]) {
    const applicationRecords = applications.filter((app) => app.recordType !== 'instance');
    const approvedStates = new Set([
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      'TAS.ApplicationState.EVIDENCE_APPROVED',
      'TAS.ApplicationState.FULFILLED',
    ]);

    const pendingStates = new Set([
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
    ]);

    const activeStates = new Set([
      'TAS.ApplicationState.ATTEND',
      'TAS.ApplicationState.COURSES_APPROVED',
    ]);

    return {
      totalApproved: applicationRecords
        .filter((app) => approvedStates.has(app.state))
        .reduce((sum, app) => sum + app.approvedTotal, 0),
      totalPaid: applicationRecords.reduce((sum, app) => sum + app.paidTotal, 0),
      pendingCount: applicationRecords.filter((app) => pendingStates.has(app.state)).length,
      activeCount: applicationRecords.filter((app) => activeStates.has(app.state)).length,
      completedCount: applicationRecords.filter(
        (app) => app.state === 'TAS.ApplicationState.FULFILLED'
      ).length,
    };
  }

  private computeTasUsedAmount(applications: LiveTasApplication[]) {
    const applicationRecords = applications.filter((app) => app.recordType !== 'instance');
    const currentYear = new Date().getFullYear();
    const approvedStates = new Set([
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      'TAS.ApplicationState.EVIDENCE_APPROVED',
      'TAS.ApplicationState.FULFILLED',
    ]);

    return applicationRecords
      .filter((app) => approvedStates.has(app.state))
      .filter((app) => {
        const appDate = new Date(app.createdAt);
        return !Number.isNaN(appDate.getTime()) && appDate.getFullYear() === currentYear;
      })
      .reduce((sum, app) => sum + app.approvedTotal, 0);
  }

  private mapSearchResultsToLiveTasData(docs: RecentApplication[]) {
    const applicationDocs = docs.filter(
      (app) => app.recordType === 'application' || app.tasApplicationIdKeyword
    );
    const instanceDocs = docs.filter((app) => app.recordType === 'instance');
    const programsById = new Map<string, LiveTasProgram>();
    const hasApplicationIds = applicationDocs.some((app) => !!app.tasApplicationIdKeyword);

    for (const app of docs) {
      const programId = app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId;
      if (!programId) {
        continue;
      }

      const existing = programsById.get(programId);
      const programName = app.tasProgramTemplateFieldProgramNameKeyword || null;
      const institution = app.tasProgramInstanceFieldSchoolInstitutionNameKeyword || null;
      const major =
        app.tasProgramInstanceFieldProgramMajorKeyword ||
        app.programMajor ||
        app.tasProgramInstanceFieldProgramSpecializationKeyword ||
        app.programSpecialization ||
        null;
      const startDate = app.tasProgramInstanceCreatedAtDate || app.createdAt || null;
      const updatedAt = app.updatedAt || null;
      const typeClassification = app.tasProgramTemplateTypeKeyword || null;
      const state = app.tasProgramInstanceStateKeyword || 'TAS.ProgramInstanceState.CREATED';

      if (!existing) {
        programsById.set(programId, {
          id: programId,
          name: programName || 'Program',
          state,
          institution,
          major,
          startDate,
          updatedAt,
          typeClassification,
        });
      } else {
        programsById.set(programId, {
          ...existing,
          name: existing.name || programName || 'Program',
          state: existing.state || state,
          institution: existing.institution || institution,
          major: existing.major || major,
          startDate: existing.startDate || startDate,
          updatedAt: existing.updatedAt || updatedAt,
          typeClassification: existing.typeClassification || typeClassification,
        });
      }
    }

    const applications = [...applicationDocs, ...instanceDocs].map((app) => {
      const recordType: 'application' | 'instance' =
        (app.recordType as 'application' | 'instance') ??
        (app.tasApplicationIdKeyword ? 'application' : 'instance');
      const programInstanceId = app.tasProgramInstanceIdKeyword || app.tasProgramInstanceId || null;
      const applicationId =
        recordType === 'application'
          ? app.tasApplicationIdKeyword || app.id
          : programInstanceId || app.id;
      const approvedTotalCents =
        app.paymentFinalApprovedTotalAmount ?? app.paymentPreApprovedTotal ?? 0;

      const createdAt =
        app.tasApplicationCreatedAtDate ||
        app.createdAt ||
        app.documentCreatedAt ||
        app.updatedAt ||
        new Date().toISOString();

      return {
        id: applicationId || programInstanceId || app.id || '',
        recordType,
        programInstanceId,
        name:
          app.tasApplicationFieldApplicationNameKeyword ||
          app.tasProgramInstanceFieldProgramSpecializationKeyword ||
          'Application',
        state: this.getSearchApplicationState(app),
        paymentState: this.derivePaymentState(app),
        approvedTotal: approvedTotalCents / 100,
        requestedTotal:
          (app.tasApplicationFieldPreApprovedTotalInteger ?? app.paymentPreApprovedTotal ?? 0) /
          100,
        paidTotal: (app.paymentPaidAmount || 0) / 100,
        courseCashPreApprovedTotal:
          app.tasApplicationFieldCardPreApprovedTotalInteger === undefined
            ? null
            : app.tasApplicationFieldCardPreApprovedTotalInteger / 100,
        courseCashFinalApprovedTotal:
          app.tasApplicationFieldCardFinalApprovedTotalInteger === undefined
            ? null
            : app.tasApplicationFieldCardFinalApprovedTotalInteger / 100,
        courseCashUsed:
          app.courseCashUsedAmount === undefined ? null : app.courseCashUsedAmount / 100,
        courseCashReserved:
          app.courseCashReservedAmount === undefined ? null : app.courseCashReservedAmount / 100,
        coursesBeginDate: app.tasApplicationFieldCoursesBeginDateDate || null,
        coursesEndDate: app.tasApplicationFieldCoursesEndDateDate || null,
        createdAt,
        updatedAt: app.updatedAt || createdAt,
        programName: app.tasProgramTemplateFieldProgramNameKeyword || null,
        programTemplateId: app.tasProgramTemplateIdKeyword || null,
      };
    });

    return {
      programs: [...programsById.values()],
      applications,
      hasApplicationIds,
      hasApplicationDocs: applicationDocs.length > 0,
    };
  }

  private derivePaymentState(app: RecentApplication) {
    if (app.paymentRefundRequestedAmount) {
      return 'TAS.ApplicationPaymentState.REFUND_REQUESTED';
    }

    if (app.paymentPaidAmount) {
      if (
        app.paymentFinalApprovedTotalAmount &&
        app.paymentPaidAmount >= app.paymentFinalApprovedTotalAmount
      ) {
        return 'TAS.ApplicationPaymentState.PAID_IN_FULL';
      }

      return 'TAS.ApplicationPaymentState.PAID_IN_PART';
    }

    return 'TAS.ApplicationPaymentState.NONE';
  }

  private getSearchApplicationState(app: RecentApplication) {
    if (app.tasApplicationStateKeyword) {
      return app.tasApplicationStateKeyword;
    }

    if (app.recordType === 'instance') {
      return isActiveInstanceState(app.tasProgramInstanceStateKeyword)
        ? 'TAS.ApplicationState.ATTEND'
        : 'TAS.ApplicationState.ABANDONED';
    }

    return 'TAS.ApplicationState.CREATED';
  }

  private async fetchTasSearchDocuments({
    employeeId,
    companyId,
  }: {
    employeeId: string;
    companyId: string | null;
  }) {
    const now = Date.now();
    if (
      this.tasSearchCache &&
      this.tasSearchCache.employeeId === employeeId &&
      this.tasSearchCache.companyId === companyId &&
      now - this.tasSearchCache.fetchedAt < this.tasSearchCacheTtlMs
    ) {
      return this.tasSearchCache.docs;
    }

    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    const builder = new OpenSearchQueryBuilder().mustTerm('employee_id', employeeId);
    if (companyId) {
      builder.mustTerm('company_id', companyId);
    }

    // Logic: Show applications/instances if they are:
    // 1. Active (regardless of date), OR
    // 2. Inactive/closed but updated within the last month
    builder
      .addFilter({
        bool: {
          should: [
            // Active applications (always show)
            { terms: { tas_application_state_keyword: ACTIVE_APPLICATION_STATES } },
            // Active program instances (always show)
            { terms: { tas_program_instance_state_keyword: ACTIVE_INSTANCE_STATES } },
            // Inactive applications updated recently
            {
              bool: {
                must: [
                  { terms: { tas_application_state_keyword: INACTIVE_APPLICATION_STATES } },
                  { range: { updated_at: { gte: oneMonthAgo.toISOString() } } },
                ],
              },
            },
            // Inactive program instances updated recently
            {
              bool: {
                must: [
                  { terms: { tas_program_instance_state_keyword: INACTIVE_INSTANCE_STATES } },
                  { range: { updated_at: { gte: oneMonthAgo.toISOString() } } },
                ],
              },
            },
          ],
          minimum_should_match: 1,
        },
      })
      .setSort('updated_at:desc')
      .setPagination(1, 200);

    const query = {
      'model-type': 'TasProgramInstance',
      filter: { 'tas-applications.with-deleted': false },
      searchBody: builder.build(),
    };

    const response = (await this.api.call('/api/v2/search', {
      method: 'POST',
      body: JSON.stringify(query),
    })) as OpenSearchResponse<RecentApplication>;

    const responseBuilder = new OpenSearchResponseBuilder<RecentApplication>(response);
    const docs = normalizePaymentFields(responseBuilder.getDocuments());

    this.tasSearchCache = {
      employeeId,
      companyId,
      fetchedAt: now,
      docs,
    };

    return docs;
  }

  private get isTasDebugEnabled() {
    if (typeof window === 'undefined') {
      return false;
    }

    return new URL(window.location.href).searchParams.get('debug') === 'true';
  }

  private getEmptyTasData(): LiveTasData {
    return {
      isEligible: this.partner.hasNewTuitionAssistance,
      participantState: null,
      programs: [],
      applications: [],
      summary: {
        totalApproved: 0,
        totalPaid: 0,
        pendingCount: 0,
        activeCount: 0,
        completedCount: 0,
      },
      isLiveData: true,
    };
  }

  /**
   * Returns a proxy object that mimics the TasApplicationModel interface for demo mode.
   * This prevents the show route from making a real API call for demo IDs like "demo-app-1".
   */
  getDemoTasApplicationProxy(applicationId: string): DemoTasApplicationProxy | null {
    const tasData = this.getDemoTasData();
    const demoApp = tasData.applications.find((app) => app.id === applicationId);
    if (!demoApp) {
      return null;
    }

    const demoProgram = tasData.programs[0] || null;

    return {
      id: demoApp.id,
      state: demoApp.state,
      paymentState: demoApp.paymentState,
      approvedTotal: demoApp.approvedTotal,
      createdAt: demoApp.createdAt,
      displayName: demoApp.name,
      activeCourses: [],
      tasCourses: [],
      tasAssets: [],
      tasProgramInstance: demoProgram
        ? {
            id: demoProgram.id,
            tasProgramTemplate: {
              programName: demoProgram.name,
              typeClassification: 'TAS.ProgramType.3',
              courseApprovalRequired: true,
            },
            fields: {},
          }
        : null,
      // Fields needed by the template
      fields: {
        APPLICATION_NAME: { values: [demoApp.name] },
        COURSES_BEGIN_DATE: { values: [demoApp.coursesBeginDate || ''] },
        COURSES_END_DATE: { values: [demoApp.coursesEndDate || ''] },
      },
      applicationName: demoApp.name,
      programName: demoApp.programName || '',
      coursesBeginDate: demoApp.coursesBeginDate || '',
      transitionLog: [],
      customFields: [],
      paymentStateLog: [],
      approvedTotalLog: [],
      coursesApproval: { approvers: [], status: 'pending' },
      evidenceApproval: { approvers: [], status: 'pending' },
      isDraft: demoApp.state === 'TAS.ApplicationState.DEFINE_COURSES',
      isApproved: demoApp.state === 'TAS.ApplicationState.COURSES_APPROVED',
      tags: [],
      attachments: [],
      _isDemoProxy: true,
    } as DemoTasApplicationProxy;
  }

  private getDemoTasData(): LiveTasData {
    const demoScenario = getCurrentDemoScenario();

    // If TA is not available in this scenario, return ineligible data
    if (!demoScenario.features.tuitionAssistance) {
      return {
        isEligible: false,
        participantState: null,
        programs: [],
        applications: [],
        summary: {
          totalApproved: 0,
          totalPaid: 0,
          pendingCount: 0,
          activeCount: 0,
          completedCount: 0,
        },
        isLiveData: false,
      };
    }

    const scenario = getCurrentDemoTasScenarioId();

    const baseProgram: LiveTasProgram = {
      id: 'demo-prog-1',
      name: 'Data Analytics Certificate',
      state: 'TAS.ProgramInstanceState.IN_PROGRESS',
      institution: 'Coursera / Google',
      major: 'Data Analytics',
      startDate: '2025-09-01',
      updatedAt: '2026-01-20',
      typeClassification: 'TAS.ProgramType.4.2',
    };

    const baseApplication: LiveTasApplication = {
      id: 'demo-app-1',
      recordType: 'application',
      programInstanceId: 'demo-prog-1',
      programTemplateId: 'demo-tpl-1',
      name: 'Fall 2025 - Data Analytics',
      state: 'TAS.ApplicationState.ATTEND',
      paymentState: 'TAS.ApplicationPaymentState.PAID_IN_PART',
      approvedTotal: 1500,
      requestedTotal: 1500,
      paidTotal: 750,
      courseCashPreApprovedTotal: null,
      courseCashFinalApprovedTotal: null,
      courseCashUsed: 0,
      courseCashReserved: 0,
      coursesBeginDate: '2025-09-01',
      coursesEndDate: '2025-12-15',
      createdAt: '2025-08-15',
      updatedAt: '2026-01-20',
      programName: 'Data Analytics Certificate',
    };

    const scenarioData: Record<
      ReturnType<typeof getCurrentDemoTasScenarioId>,
      { programs: LiveTasProgram[]; applications: LiveTasApplication[] }
    > = {
      'not-started': {
        programs: [],
        applications: [],
      },
      'in-progress': {
        programs: [baseProgram],
        applications: [
          baseApplication,
          {
            id: 'demo-app-2',
            recordType: 'application',
            programInstanceId: 'demo-prog-1',
            programTemplateId: 'demo-tpl-1',
            name: 'AWS Certification',
            state: 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
            paymentState: 'TAS.ApplicationPaymentState.NONE',
            approvedTotal: 0,
            requestedTotal: 0,
            paidTotal: 0,
            courseCashPreApprovedTotal: null,
            courseCashFinalApprovedTotal: null,
            courseCashUsed: 0,
            courseCashReserved: 0,
            coursesBeginDate: '2026-02-01',
            coursesEndDate: '2026-04-30',
            createdAt: '2026-01-20',
            updatedAt: '2026-01-20',
            programName: 'Professional Certifications',
          },
        ],
      },
      approved: {
        programs: [baseProgram],
        applications: [
          {
            ...baseApplication,
            state: 'TAS.ApplicationState.COURSES_APPROVED',
            paymentState: 'TAS.ApplicationPaymentState.NONE',
            approvedTotal: 2400,
            requestedTotal: 2400,
            paidTotal: 0,
            updatedAt: '2026-01-20',
          },
        ],
      },
      rejected: {
        programs: [baseProgram],
        applications: [
          {
            ...baseApplication,
            state: 'TAS.ApplicationState.COURSES_REJECTED',
            paymentState: 'TAS.ApplicationPaymentState.NONE',
            approvedTotal: 0,
            requestedTotal: 1500,
            paidTotal: 0,
            updatedAt: '2026-01-20',
          },
        ],
      },
      completed: {
        programs: [
          {
            ...baseProgram,
            state: 'TAS.ProgramInstanceState.COMPLETED',
          },
        ],
        applications: [
          {
            ...baseApplication,
            state: 'TAS.ApplicationState.FULFILLED',
            paymentState: 'TAS.ApplicationPaymentState.PAID',
            approvedTotal: 3000,
            requestedTotal: 3000,
            paidTotal: 3000,
            updatedAt: '2026-01-20',
          },
        ],
      },
    };

    const { programs, applications } = scenarioData[scenario];

    const summary = {
      totalApproved: applications.reduce((sum, app) => sum + app.approvedTotal, 0),
      totalPaid: applications.reduce((sum, app) => sum + app.paidTotal, 0),
      pendingCount: applications.filter((app) =>
        [
          'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
          'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
        ].includes(app.state)
      ).length,
      activeCount: applications.filter((app) =>
        ['TAS.ApplicationState.ATTEND', 'TAS.ApplicationState.COURSES_APPROVED'].includes(app.state)
      ).length,
      completedCount: applications.filter((app) => app.state === 'TAS.ApplicationState.FULFILLED')
        .length,
    };

    return {
      isEligible: true,
      participantState: 'TAS.ParticipantState.ACTIVE',
      programs,
      applications,
      summary,
      isLiveData: false,
    };
  }

  /**
   * Fetches comprehensive benefits summary combining SLR, TAS, and PSLF
   */
  async fetchBenefitsSummary() {
    const [contributionsData, tasData, pslfData, section127Info] = await Promise.all([
      this.fetchEmployerContributions(),
      this.fetchTasData(),
      this.fetchPslfData(),
      this.fetchSection127Info(),
    ]);

    return {
      section127: section127Info,
      slr: {
        enrolled: contributionsData.isLiveData && contributionsData.contributions.yearToDate > 0,
        yearToDate: contributionsData.contributions.yearToDate,
        lifetime: contributionsData.contributions.lifetime,
        isLiveData: contributionsData.isLiveData,
      },
      tas: {
        enrolled: tasData.applications.length > 0,
        ...tasData.summary,
        isLiveData: tasData.isLiveData,
      },
      pslf: {
        enrolled: pslfData.isEnrolled,
        qualifyingPayments: pslfData.qualifyingPayments,
        estimatedForgiveness: pslfData.estimatedForgiveness,
        isLiveData: pslfData.isLiveData,
      },
      hasAnyBenefits:
        contributionsData.contributions.yearToDate > 0 ||
        tasData.applications.length > 0 ||
        pslfData.isEnrolled,
    };
  }

  async fetchLrapDiscovery() {
    return this.careerPathwaysApi.fetchLrapDiscovery();
  }

  /**
   * Fetches eligible program templates for the Eligible Benefits section.
   * In demo mode, returns static demo templates.
   * In live mode, queries for LAUNCHED templates filtered by employee eligibility.
   */
  async fetchEligibleTemplates(enrolledTemplateIds?: Set<string>): Promise<EligibleTemplate[]> {
    if (this.isDemoMode) {
      return this.getDemoEligibleTemplates();
    }

    const employee = this.sessionContext.currentEmployee;
    if (!employee?.id) {
      return [];
    }

    const companyId = employee.company?.id;
    if (!companyId) {
      return [];
    }

    try {
      const hasTas = this.partner.hasNewTuitionAssistance;
      if (!hasTas) {
        return [];
      }

      let templates = await this.store.query<TasProgramTemplateModel>('tas-program-template', {
        filter: {
          state: 'TAS.ProgramTemplateState.LAUNCHED',
          byEligibility: employee.id,
          company: companyId,
        },
        include: 'company.company-setting',
        reload: true,
        page: { limit: 100 },
      });

      // Get participant for eligibility checks
      let participant = employee.tasParticipant;
      if (!participant) {
        const employeeRecord = (await this.store.findRecord('employee', employee.id, {
          include: 'tas-participant,company',
          reload: true,
        })) as EmployeeModel;
        participant = employeeRecord?.tasParticipant;
      }

      const tasParticipantId = participant?.id;

      // Filter by dynamic eligibility and period of eligibility
      if (tasParticipantId) {
        const [dynamicEligibility, programEligibility] = await Promise.all([
          this.store.adapterFor('tas-participant').dynamicEligibility(tasParticipantId),
          this.store.adapterFor('tas-participant').periodOfEligibility(tasParticipantId),
        ]);

        templates = filterEligibleTemplates(
          [...templates],
          dynamicEligibility,
          programEligibility
        ) as unknown as typeof templates;
      }

      // Filter out dependent programs (only show employee programs)
      const enrolledIds = enrolledTemplateIds ?? new Set<string>();

      return [...templates]
        .filter((t) => !t.isDependentProgram)
        .map((t) => ({
          id: t.id || '',
          name: t.programName || 'Program',
          description: t.programDescription || null,
          isDirectPayProgram: t.isDirectPayProgram,
          isEnrolled: t.id ? enrolledIds.has(t.id) : false,
        }));
    } catch (error) {
      console.warn('Failed to fetch eligible templates:', error);
      return [];
    }
  }

  private getDemoEligibleTemplates(): EligibleTemplate[] {
    const demoScenario = getCurrentDemoScenario();

    if (!demoScenario.features.tuitionAssistance) {
      return [];
    }

    return [
      {
        id: 'demo-tpl-1',
        name: 'Professional Certifications Program',
        description:
          'Earn industry-recognized certifications in your field. Covers exam fees, prep materials, and study courses.',
        isDirectPayProgram: false,
        isEnrolled: true,
      },
      {
        id: 'demo-tpl-2',
        name: 'Degree Completion Program',
        description:
          "Complete your bachelor's or master's degree at an accredited institution with tuition reimbursement.",
        isDirectPayProgram: false,
        isEnrolled: false,
      },
      {
        id: 'demo-tpl-3',
        name: 'Skills Development CourseCA$H',
        description:
          'Access pre-approved online courses and bootcamps with direct payment through CourseCA$H.',
        isDirectPayProgram: true,
        isEnrolled: false,
      },
    ];
  }

  async fetchLrapPrograms() {
    return this.careerPathwaysApi.fetchLrapPrograms();
  }

  async fetchCareerFamilies() {
    return this.careerPathwaysApi.fetchCareerFamilies();
  }

  async fetchFeaturedRoles() {
    return this.careerPathwaysApi.fetchFeaturedRoles();
  }

  async fetchCareerRole(roleId: string) {
    return this.careerPathwaysApi.fetchCareerRole(roleId);
  }

  async fetchRolePathways(roleId: string) {
    return this.careerPathwaysApi.fetchRolePathways(roleId);
  }

  async fetchAllRolesForComparison() {
    return this.careerPathwaysApi.fetchAllRolesForComparison();
  }

  async fetchAssessmentQuestions(skillId: string) {
    return this.careerPathwaysApi.fetchAssessmentQuestions(skillId);
  }

  async fetchEmployeeSkills() {
    return this.careerPathwaysApi.fetchEmployeeSkills();
  }

  async fetchSkillGaps() {
    return this.careerPathwaysApi.fetchSkillGaps();
  }

  async fetchCareerPlan() {
    return this.careerPathwaysApi.fetchCareerPlan();
  }

  async fetchAvailableGoals() {
    return this.careerPathwaysApi.fetchAvailableGoals();
  }

  async fetchRecommendations() {
    return this.careerPathwaysApi.fetchRecommendations();
  }

  async fetchSuccessStories() {
    return this.careerPathwaysApi.fetchSuccessStories();
  }

  async fetchPslfVault() {
    return this.careerPathwaysApi.fetchPslfVault();
  }
}
